import React, { useEffect, useState, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Tooltip from '../components/Tooltip';

interface DataApp {
  title: string;
  description: string;
  imageUrl: string;
  appUrl: string;
  tags?: string[];
}

const dataApps: DataApp[] = [
  {
    title: "cGEM-Forge",
    description: "Community Genome-Scale Metabolic Modeling platform for predicting metabolic interactions and nutrient cycling patterns in marine microbial communities.",
    imageUrl: "/images/data-apps/cgem-forge.png",
    appUrl: "https://cgemforge.oceanbiometrics.com",
    tags: ["Metabolic Modeling", "Community Analysis", "Marine Microbiomes"]
  },
  {
    title: "ProtScout",
    description: "AI-powered enzyme discovery and characterization platform for rapid in silico functional analysis and ranking of protein sequences from marine metagenomes.",
    imageUrl: "/images/data-apps/protscout.png",
    appUrl: "https://protscout.oceanbiometrics.com",
    tags: ["Enzyme Discovery", "Protein Analysis", "AI/ML"]
  },
  {
    title: "SeDNA",
    description: "Biosynthetic Gene Cluster (BGC) analysis platform for natural product discovery, featuring interactive network visualization and diversity analysis.",
    imageUrl: "/images/data-apps/sedna.png",
    appUrl: "https://sedna.oceanbiometrics.com",
    tags: ["Natural Products", "BGC Analysis", "Drug Discovery"]
  },
  {
    title: "Metabolic Niche Space",
    description: "Interactive visualization platform for exploring the metabolic diversity and functional potential of marine microorganisms across ocean ecosystems.",
    imageUrl: "/images/data-apps/mns.png",
    appUrl: "https://mns.oceanbiometrics.com",
    tags: ["Metabolic Diversity", "Visualization", "Ocean Genomics"]
  },
  {
    title: "MicroCosm Analytics",
    description: "Comprehensive microbiome analytics engine for multi-kingdom analysis, featuring end-to-end processing from raw sequencing data to functional insights.",
    imageUrl: "/images/data-apps/microcosm.png",
    appUrl: "https://microcosm.oceanbiometrics.com",
    tags: ["Microbiome Analysis", "Multi-kingdom", "Metagenomics"]
  },
  {
    title: "Proteus",
    description: "AI-powered protein engineering platform that accelerates in silico directed evolution using deep learning and multi-objective optimization to rapidly design and optimize enzyme variants for enhanced catalytic activity, stability, and binding affinity.",
    imageUrl: "/images/data-apps/proteus.png",
    appUrl: "https://proteus.oceanbiometrics.com",
    tags: ["Protein Engineering", "Directed Evolution", "AI/ML"]
  }
];

const DataCatalogEmbed: React.FC = () => {
  const [iframeError, setIframeError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Detect mobile devices
    const checkMobile = () => {
      const userAgent = navigator.userAgent;
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Suppress iframe console errors from external data catalog
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const message = args.join(' ');
      // Filter out known iframe errors from data catalog
      if (message.includes('Failed to fetch') ||
        message.includes('getTileData') ||
        message.includes('index.Ch7MBCx0.js')) {
        return; // Suppress these specific errors
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      window.removeEventListener('resize', checkMobile);
      observer.disconnect();
      console.error = originalConsoleError; // Restore original console.error
    };
  }, []);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIframeError(true);
    setIsLoading(false);
  };

  // Mobile optimized height
  const getHeight = () => {
    if (isMobile) {
      return Math.min(300, window.innerHeight * 0.4);
    }
    return 600;
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {isLoading && isVisible && !isMobile && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#18C3BA',
          fontSize: '1.1rem',
          zIndex: 10
        }}>
          Loading interactive map...
        </div>
      )}

      {/* Mobile: Show static image with link instead of iframe */}
      {isMobile ? (
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer'
        }}
          onClick={() => window.open('https://data-catalog.oceanbiometrics.com/', '_blank', 'noopener,noreferrer')}
        >
          <img
            src="/images/data-apps/ocean-data-map.png"
            alt="Ocean BioMetrics Global Dataset Distribution"
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '8px',
              marginBottom: '12px'
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              if (e.currentTarget.parentElement) {
                const textFallback = document.createElement('div');
                textFallback.innerHTML = `
                  <h3 style="color: #EFEFE3; margin-bottom: 1rem; font-family: 'Montserrat', sans-serif; font-size: 1.2rem; text-align: center;">Interactive Data Catalog</h3>
                  <p style="color: #D5D6CC; text-align: center; max-width: 300px; font-size: 0.9rem; margin-bottom: 12px;">
                    Tap to open the full interactive map in a new window
                  </p>
                `;
                textFallback.style.textAlign = 'center';
                e.currentTarget.parentElement.appendChild(textFallback);
              }
            }}
          />
          <div style={{
            background: 'linear-gradient(90deg, #0888D0, #18C3BA)',
            color: '#EFEFE3',
            padding: '8px 16px',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: 600,
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(8, 136, 208, 0.3)'
          }}>
            Tap to Open Full Interactive Map →
          </div>
        </div>
      ) : (
        // Desktop: Show iframe only when visible
        !iframeError && isVisible ? (
          <iframe
            src="https://data-catalog.oceanbiometrics.com/"
            style={{
              width: '100%',
              height: `${getHeight()}px`,
              border: 'none',
              borderRadius: '8px',
              background: '#0A0E14'
            }}
            title="Ocean BioMetrics Data Catalog"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            loading="lazy"
          />
        ) : !isVisible ? (
          // Placeholder before iframe loads
          <div style={{
            width: '100%',
            height: `${getHeight()}px`,
            background: 'linear-gradient(135deg, rgba(8, 136, 208, 0.1) 0%, rgba(24, 195, 186, 0.1) 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(239, 239, 227, 0.08)'
          }}>
            <div style={{ color: '#18C3BA', fontSize: '1.1rem' }}>
              Scroll down to load interactive map...
            </div>
          </div>
        ) : (
          // Desktop: Iframe error fallback
          <div style={{
            width: '100%',
            height: `${getHeight()}px`,
            background: 'linear-gradient(135deg, rgba(8, 136, 208, 0.1) 0%, rgba(24, 195, 186, 0.1) 100%)',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(239, 239, 227, 0.08)'
          }}>
            <img
              src="/images/data-apps/ocean-data-map.png"
              alt="Ocean BioMetrics Global Dataset Distribution"
              style={{
                maxWidth: '100%',
                maxHeight: '90%',
                objectFit: 'contain',
                borderRadius: '4px'
              }}
              onError={(e) => {
                // If fallback image also fails, show text
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.parentElement) {
                  const textFallback = document.createElement('div');
                  textFallback.innerHTML = `
                    <h3 style="color: #EFEFE3; margin-bottom: 1rem; font-family: 'Montserrat', sans-serif;">Interactive Data Catalog</h3>
                    <p style="color: #D5D6CC; text-align: center; max-width: 400px;">
                      Our global dataset distribution map is temporarily unavailable.
                      <br><br>
                      <a href="https://data-catalog.oceanbiometrics.com/" target="_blank" rel="noopener noreferrer"
                         style="color: #18C3BA; text-decoration: none;">
                        View Full Catalog →
                      </a>
                    </p>
                  `;
                  textFallback.style.textAlign = 'center';
                  e.currentTarget.parentElement.appendChild(textFallback);
                }
              }}
            />
          </div>
        )
      )}
    </div>
  );
};

const DataAppsPage: React.FC = () => {
  useEffect(() => {
    // Always scroll to top when this page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="d-flex flex-column min-vh-100" style={{
      background: 'linear-gradient(135deg, #0A0E14 0%, #14191F 30%, #1E252D 70%, #2A3541 100%)',
      backgroundAttachment: 'fixed'
    }}>
      <Header isMainPage={false} />

      <main className="flex-grow-1">
        <section className="data-apps-grid py-5" style={{
          marginTop: '60px',
          position: 'relative'
        }}>
          <style>
            {`.data-apps-grid::before { display: none; }
             @media (max-width: 767.98px) {
               .datasets-list {
                 columns: auto 1 !important;
               }
             }`}
          </style>
          <div className="container">
            {/* Header Section */}
            <div className="row justify-content-center mb-5">
              <div className="col-lg-10 text-center">
                <h1 style={{
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  fontWeight: 700,
                  marginBottom: '2rem',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  color: '#EFEFE3',
                  fontFamily: "'Montserrat', sans-serif"
                }}>
                  Resources
                </h1>
                <p style={{
                  fontSize: '1.25rem',
                  color: '#D5D6CC',
                  lineHeight: '1.8',
                  maxWidth: '900px',
                  margin: '0 auto',
                  marginBottom: '3rem'
                }}>
                  Access powerful computational tools that turn ocean genomic data into breakthrough discoveries. Our evolving platform suite combines machine learning, metabolic modeling, and advanced visualization to reveal the functional potential of marine ecosystems and accelerate sustainable <Tooltip definition="Bioprospecting is the search for commercially valuable products by analyzing the genetic material (genomes) of organisms found in nature, especially microbes. This process can reveal novel genes, enzymes, peptides, or pathways with applications in biotechnology, bioremediation, medicine, and industry." term="Bioprospecting">bioprospecting</Tooltip>. These tools are customized and combined to deliver the precise insights our clients need.
                </p>
              </div>
            </div>

            <div className="row g-5 justify-content-center">
              {dataApps.map((app, index) => (
                <div key={index} className="col-sm-12 col-md-6 col-lg-4">
                  <div className="app-card h-100" style={{
                    background: 'rgba(239, 239, 227, 0.04)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(239, 239, 227, 0.08)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    transition: 'all 0.4s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 30px rgba(8, 136, 208, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(8, 136, 208, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)';
                      e.currentTarget.style.borderColor = 'rgba(239, 239, 227, 0.08)';
                    }}>
                    <div style={{
                      height: '260px',
                      background: 'linear-gradient(135deg, rgba(8, 136, 208, 0.1) 0%, rgba(24, 195, 186, 0.1) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      borderBottom: '1px solid rgba(239, 239, 227, 0.08)'
                    }}>
                      <img
                        src={app.imageUrl}
                        alt={app.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.parentElement) {
                            const placeholder = document.createElement('div');
                            placeholder.innerHTML = `<h3 style="color: #EFEFE3; font-size: 1.8rem; font-weight: 700; font-family: 'Montserrat', sans-serif;">${app.title}</h3>`;
                            placeholder.style.textAlign = 'center';
                            e.currentTarget.parentElement.appendChild(placeholder);
                          }
                        }}
                      />
                    </div>

                    <div className="card-body p-4" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <h3 className="mb-3" style={{
                        color: '#EFEFE3',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        fontFamily: "'Montserrat', sans-serif"
                      }}>
                        {app.title}
                      </h3>

                      <p className="mb-3" style={{
                        fontSize: '0.95rem',
                        lineHeight: '1.6',
                        flex: 1,
                        color: '#D5D6CC'
                      }}>
                        {app.description}
                      </p>

                      {app.tags && (
                        <div className="mb-3">
                          {app.tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="badge me-2 mb-2"
                              style={{
                                background: 'rgba(8, 136, 208, 0.1)',
                                color: '#18C3BA',
                                padding: '0.4rem 0.8rem',
                                fontSize: '0.75rem',
                                border: '1px solid rgba(24, 195, 186, 0.3)',
                                borderRadius: '20px'
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: 'auto'
                      }}>
                        <a
                          href={app.appUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn"
                          style={{
                            background: 'linear-gradient(90deg, #0888D0, #18C3BA)',
                            border: 'none',
                            color: '#EFEFE3',
                            padding: '0.75rem 2rem',
                            borderRadius: '25px',
                            fontWeight: 600,
                            fontFamily: "'Inter', 'Roboto', sans-serif",
                            transition: 'all 0.3s ease',
                            textDecoration: 'none',
                            display: 'inline-block',
                            textAlign: 'center',
                            minWidth: '150px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 0 20px rgba(8, 136, 208, 0.4)';
                            e.currentTarget.style.background = 'linear-gradient(90deg, #18C3BA, #0888D0)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.background = 'linear-gradient(90deg, #0888D0, #18C3BA)';
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          Launch App →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Datasets Section */}
            <div className="row justify-content-center mt-5 pt-4">
              <div className="col-12">
                <h2 className="text-center mb-3" style={{
                  fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                  fontWeight: 700,
                  color: '#EFEFE3',
                  fontFamily: "'Montserrat', sans-serif"
                }}>
                  Key Datasets
                </h2>

                <div className="row justify-content-center mb-4">
                  <div className="col-lg-10">
                    <p style={{
                      fontSize: '1.25rem',
                      color: '#D5D6CC',
                      lineHeight: '1.8',
                      textAlign: 'center',
                      marginBottom: '2rem'
                    }}>
                      Access our comprehensive ocean genomic repositories spanning from biosynthetic gene clusters to longitudinal ecosystem monitoring. Our curated datasets power discovery pipelines, track conservation outcomes, and unlock the functional potential of marine biodiversity at unprecedented scales. Thanks to the advanced methods we have developed and implemented, we recover 2–8× more high-quality prokaryotic metagenome-assembled genomes (MAGs) and identify 2–6× more high-confidence biosynthetic gene clusters (BGCs) compared to standard approaches.
                    </p>
                  </div>
                </div>

                <div className="row justify-content-center">
                  <div className="col-12">
                    {/* Interactive Data Catalog Map */}
                    <div className="mb-5">
                      <div style={{
                        background: 'rgba(239, 239, 227, 0.04)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(239, 239, 227, 0.08)',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        marginBottom: '1rem'
                      }}>
                        <DataCatalogEmbed />
                      </div>
                      <p style={{
                        fontSize: '0.9rem',
                        color: 'rgba(213, 214, 204, 0.6)',
                        lineHeight: '1.6',
                        textAlign: 'center',
                        fontStyle: 'italic',
                        marginBottom: 0,
                        padding: '0 2rem'
                      }}>
                        Main metagenomic samples from the Ocean BioMetrics data catalog, derived from publicly available sources and processed with our analysis platform. Hover over each location to see the number of reconstructed microbial genomes (MAGs), natural product gene clusters (BGCs), and gene cluster families (GCFs) recovered at each location.
                      </p>
                    </div>

                    {/* Datasets List */}
                    <div className="datasets-grid" style={{
                      background: 'rgba(239, 239, 227, 0.04)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(239, 239, 227, 0.08)',
                      borderRadius: '12px',
                      padding: '2rem',
                      marginBottom: '3rem'
                    }}>
                      <ul className="datasets-list" style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        fontSize: '1.1rem',
                        color: '#D5D6CC',
                        lineHeight: '2',
                        columns: 'auto 2',
                        columnGap: '3rem',
                        columnFill: 'balance'
                      }}>
                        <li style={{ marginBottom: '1.5rem', breakInside: 'avoid' }}>
                          <strong style={{ color: '#18C3BA' }}>BGCs Catalogue:</strong> Comprehensive catalog of Biosynthetic Gene Clusters from marine environments, featuring novel natural product discovery potential
                        </li>
                        <li style={{ marginBottom: '1.5rem', breakInside: 'avoid' }}>
                          <strong style={{ color: '#18C3BA' }}>Marine Metabolic Networks:</strong> Curated metabolic pathway maps and interaction networks from diverse ocean ecosystems
                        </li>
                        <li style={{ marginBottom: '1.5rem', breakInside: 'avoid' }}>
                          <strong style={{ color: '#18C3BA' }}>Ocean Enzyme Database:</strong> Functionally characterized marine enzymes with biotechnology applications
                        </li>
                        <li style={{ marginBottom: '1.5rem', breakInside: 'avoid' }}>
                          <strong style={{ color: '#18C3BA' }}>Biodiversity Baselines:</strong> Reference datasets for Marine Protected Areas and conservation monitoring
                        </li>
                        <li style={{ breakInside: 'avoid' }}>
                          <strong style={{ color: '#18C3BA' }}>Time-Series Genomics:</strong> Longitudinal metagenomic data tracking ecosystem changes and conservation outcomes
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-5 pt-4">
              <p className="mb-4" style={{ fontSize: '1.2rem', color: '#D5D6CC' }}>
                For full access to our enterprise platforms and custom solutions, connect with our team.
              </p>
              <a
                href="/#contact"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 2.5rem',
                  background: 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(24, 195, 186, 0.3)',
                  borderRadius: '50px',
                  color: '#18C3BA',
                  textDecoration: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  fontFamily: "'Montserrat', sans-serif",
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 8px 25px rgba(24, 195, 186, 0.15)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.25) 0%, rgba(24, 195, 186, 0.35) 100%)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 35px rgba(24, 195, 186, 0.25)';
                  e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(24, 195, 186, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.3)';
                }}
              >
                <span>Get in Touch</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{
                    transition: 'transform 0.3s ease'
                  }}
                >
                  <path
                    d="M7 17L17 7M17 7H7M17 7V17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DataAppsPage;